import React, { useContext, useMemo, useState } from "react";
import {
  Box,
  Button,
  LinearProgress,
  Link,
  Stack,
  Typography
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import InnerHTML from "dangerously-set-inner-html";
import { getJobByIdQuery } from "../../Graphql/querries/jobQuery";
import { getJobByIdResponse } from "../../Graphql/types/job";
import { fetchGraphQL } from "../../Graphql/utils";
import AlertBannerContext from "../../Contexts/alertBannerContext";
import Modal from "../Common/Modal/Modal";
import "./styles/jd.css";
import { JobDetailConfig } from "./config/jobDetailConfig";
import EditButton from "../Common/EditButton";
import EditJob from "./EditJob";
import { exportHtmlToDoc } from "./utils/jobDetailUtils";
import { APPLICATION_LIST } from "../Routes";
import WithAuth from "../../Hoc/WithAuth";

const JobDetail = () => {
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const [openJd, setOpenJd] = useState(false);
  const handleOpenJd = () => setOpenJd(true);
  const handleCloseJd = () => setOpenJd(false);

  const navigate = useNavigate();
  const containerStyle = useMemo(
    () => ({ width: "100%", height: "calc(100vh - 100px)" }),
    []
  );
  // Extract the 'id' from the URL parameters
  const { jobId } = useParams<{ jobId: string }>();
  const { setAlert } = useContext(AlertBannerContext);
  const { data, isFetching } = useQuery<getJobByIdResponse>({
    queryKey: ["getJobById"],
    queryFn: () => fetchGraphQL(getJobByIdQuery, { input: jobId }, setAlert)
  });

  if (isFetching) {
    return (
      <Stack width="100%" paddingBottom="1em" alignSelf="center">
        <LinearProgress />
      </Stack>
    );
  }

  if (!data?.getJobById) {
    return <Typography>Job doesn&apos;t exist</Typography>;
  }

  const handleDownloadJd = async (jobDescription: string) => {
    try {
      exportHtmlToDoc(jobDescription, data.getJobById.title);
    } catch (error) {
      /* eslint-disable no-console */
      console.error("Error downloading job description:", error);
    }
  };

  return (
    <section style={containerStyle}>
      <Stack
        style={{
          display: "flex",
          justifyContent: "center",
          flexDirection: "row",
          width: "100%",
          padding: "1em"
        }}
      >
        <Typography variant="h6" fontWeight="bold">
          Job Detail
        </Typography>
      </Stack>
      <Stack
        style={{
          display: "flex",
          justifyContent: "space-between",
          flexDirection: "row",
          width: "100%",
          padding: "2em",
          boxSizing: "border-box"
        }}
      >
        <Button
          onClick={() => navigate(-1)}
          variant="text"
          startIcon={<ArrowBackIcon />}
        >
          back
        </Button>
        <Stack gap={3} flexDirection="row">
          <Button
            variant="contained"
            onClick={() => navigate(`${APPLICATION_LIST}?jobId=${jobId}`)}
          >
            View Applications
          </Button>
          <EditButton onClick={handleOpen} />
        </Stack>
      </Stack>
      <Stack
        style={{
          background: "white",
          paddingBottom: "2em",
          gap: "1em",
          display: "flex",
          width: "100%",
          boxSizing: "border-box",
          flexDirection: "column"
        }}
      >
        <Box
          sx={{
            width: "100%",
            height: "auto",
            display: "flex",
            flexDirection: "row",
            flexWrap: "wrap"
          }}
        >
          {JobDetailConfig.map((config) => (
            <Stack
              key={config.key}
              style={{
                border: "solid 0.5px grey",
                padding: "1em",
                boxSizing: "border-box",
                display: "flex",
                flexDirection: "row",
                justifyContent: "start",
                alignItems: "center",
                gap: "1em",
                width: "50%"
              }}
            >
              <Typography variant="body1" fontWeight="bold">
                {`${config.displayTitle}: `}
              </Typography>
              {config.key === "jobImage" ? (
                <Link
                  href={data.getJobById[config.key]}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Click here
                </Link>
              ) : (
                <div>
                  {config.key === "description" ? (
                    <Button variant="text" onClick={() => handleOpenJd()}>
                      Click to view description
                    </Button>
                  ) : (
                    <Typography variant="body2">
                      {data.getJobById[config.key]}
                    </Typography>
                  )}
                </div>
              )}
            </Stack>
          ))}
        </Box>
      </Stack>
      {open && (
        <Modal title="Edit Job detail.." closeButton={handleClose}>
          <EditJob jobDetail={data.getJobById} handleClose={handleClose} />
        </Modal>
      )}

      {openJd && (
        <Modal title="View Job detail" closeButton={handleCloseJd}>
          <Stack
            className="jobDescriptionModalContainer"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              flexDirection: "column",
              boxSizing: "border-box",
              padding: "0 2em",
              maxWidth: "100%",
              maxHeight: "85vh"
            }}
          >
            <section
              style={{
                width: "100%",
                display: "flex",
                justifyContent: "right",
                padding: "2em"
              }}
            >
              <Button
                variant="outlined"
                onClick={() => handleDownloadJd(data.getJobById.description)}
              >
                Download as a Word doc to edit
              </Button>
            </section>
            <InnerHTML html={data.getJobById.description} />
          </Stack>
        </Modal>
      )}
    </section>
  );
};

export default WithAuth(JobDetail);
